import { useEffect, useMemo, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { IconDeviceFloppy, IconPlus, IconX } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { getIn, useFormik } from "formik";
import * as yup from "yup";
import "quill/dist/quill.snow.css";
import InlineError from "@components/forms/InlineError";
import MediaPicker from "@components/media/MediaPicker";
import AppModal from "@components/modals/AppModal";
import ConfirmDeleteModal from "@components/modals/ConfirmDeleteModal";
import HomeLocalizedField from "@components/home_content/fields/HomeLocalizedField";
import SortableCarbonItem from "@components/home_content/sections/carbon/SortableCarbonItem";
import useLanguages from "@hooks/languages/useLanguages";
import { createMultimediaRequest } from "@services/multimedia/multimediaApi";
import {
  deleteOnBoardingRequest,
  listOnBoardingsRequest,
  saveOnBoardingsRequest,
} from "@services/onBoardings/onBoardingsApi";
import { inferMediaType, resolveMediaUrl } from "@utils/media/mediaUrl";
import {
  createQuillEditor,
  destroyQuillEditor,
  getQuillHtml,
} from "@utils/quill/editor";

const LOCALIZED_ON_BOARDING_FIELDS = [
  { name: "heading", label: "heading" },
  { name: "title", label: "title" },
  { name: "subTitle", label: "sub title" },
  { name: "primaryBtn", label: "primary btn" },
];

function buildLanguageList(languages = []) {
  return languages
    .map((language) => ({
      id: language.lng_id || language.id,
      iso: (language.lng_iso || language.iso || "").toLowerCase(),
      name: language.lng_name || language.name || "",
    }))
    .filter((language) => language.iso);
}

function createClientId() {
  return `onboarding-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createEmptyLocalizedText(languageList = []) {
  return languageList.reduce((acc, language) => {
    acc[language.iso] = {
      id: "",
      text: "",
    };
    return acc;
  }, {});
}

function createEmptyLocalizedAudio(languageList = []) {
  return languageList.reduce((acc, language) => {
    acc[language.iso] = {
      id: "",
      url: "",
      media: null,
    };
    return acc;
  }, {});
}

function createEmptyOnBoardingItem(languageList = [], order = 1) {
  return {
    clientId: createClientId(),
    id: "",
    idText: "",
    order,
    lottieUrl: "",
    lottieFile: null,
    audio: createEmptyLocalizedAudio(languageList),
    heading: createEmptyLocalizedText(languageList),
    title: createEmptyLocalizedText(languageList),
    subTitle: createEmptyLocalizedText(languageList),
    primaryBtn: createEmptyLocalizedText(languageList),
  };
}

function normalizeLocalizedText(value, languageList = []) {
  return languageList.reduce((acc, language) => {
    const localizedValue = value?.[language.iso];
    if (
      localizedValue &&
      typeof localizedValue === "object" &&
      !Array.isArray(localizedValue)
    ) {
      acc[language.iso] = {
        id: localizedValue.id || "",
        text: localizedValue.text || "",
      };
      return acc;
    }
    acc[language.iso] = {
      id: "",
      text: typeof localizedValue === "string" ? localizedValue : "",
    };
    return acc;
  }, {});
}

function normalizeLocalizedAudio(value, languageList = []) {
  return languageList.reduce((acc, language) => {
    const localizedValue = value?.[language.iso];
    const url = localizedValue?.url || "";
    acc[language.iso] = {
      id: localizedValue?.id || "",
      url,
      media: url
        ? {
            id: localizedValue?.id || "",
            sourceId: localizedValue?.id || "",
            url: resolveMediaUrl(url),
            mediaType: inferMediaType(url),
            mimeType: "",
          }
        : null,
    };
    return acc;
  }, {});
}

function normalizeOnBoardingItems(items = [], languageList = []) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    clientId: createClientId(),
    id: item?.id || "",
    idText: item?.id_text || item?.idText || "",
    order: Number(item?.order) || index + 1,
    lottieUrl: item?.lottie_url || item?.lottieUrl || "",
    lottieFile:
      item?.lottie_url || item?.lottieUrl
        ? {
            id: "",
            sourceId: "",
            url: resolveMediaUrl(item?.lottie_url || item?.lottieUrl || ""),
            mediaType: inferMediaType(
              item?.lottie_url || item?.lottieUrl || "",
            ),
            mimeType: "",
          }
        : null,
    audio: normalizeLocalizedAudio(item?.audio, languageList),
    heading: normalizeLocalizedText(item?.heading, languageList),
    title: normalizeLocalizedText(item?.title, languageList),
    subTitle: normalizeLocalizedText(
      item?.sub_title || item?.subTitle,
      languageList,
    ),
    primaryBtn: normalizeLocalizedText(
      item?.primary_btn || item?.primaryBtn,
      languageList,
    ),
  }));
}

function buildInitialValues(languageList = [], items = []) {
  return {
    items: normalizeOnBoardingItems(items, languageList),
  };
}

function buildValidationSchema(languageList = []) {
  const localizedTextShape = languageList.reduce((acc, language) => {
    acc[language.iso] = yup.object({
      id: yup.string().default(""),
      text: yup
        .string()
        .trim()
        .required(`${language.iso.toUpperCase()} is required.`),
    });
    return acc;
  }, {});

  const localizedAudioShape = languageList.reduce((acc, language) => {
    acc[language.iso] = yup.object({
      id: yup.string().default(""),
      url: yup
        .string()
        .trim()
        .required(`${language.iso.toUpperCase()} audio is required.`),
    });
    return acc;
  }, {});

  return yup.object({
    items: yup.array().of(
      yup.object({
        idText: yup.string().trim().required("Id text is required."),
        lottieUrl: yup.string().trim().required("Lottie file is required."),
        audio: yup.object().shape(localizedAudioShape),
        heading: yup.object().shape(localizedTextShape),
        title: yup.object().shape(localizedTextShape),
        subTitle: yup.object().shape(localizedTextShape),
        primaryBtn: yup.object().shape(localizedTextShape),
      }),
    ),
  });
}

function buildTouchedValues(items = [], languageList = []) {
  return {
    items: items.map(() => ({
      idText: true,
      lottieUrl: true,
      audio: languageList.reduce((acc, language) => {
        acc[language.iso] = { url: true };
        return acc;
      }, {}),
      heading: languageList.reduce((acc, language) => {
        acc[language.iso] = { text: true };
        return acc;
      }, {}),
      title: languageList.reduce((acc, language) => {
        acc[language.iso] = { text: true };
        return acc;
      }, {}),
      subTitle: languageList.reduce((acc, language) => {
        acc[language.iso] = { text: true };
        return acc;
      }, {}),
      primaryBtn: languageList.reduce((acc, language) => {
        acc[language.iso] = { text: true };
        return acc;
      }, {}),
    })),
  };
}

function buildOnBoardingPayload(items = [], languageList = []) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    id: item?.id || "",
    id_text: item?.idText || "",
    order: index + 1,
    lottie_url: item?.lottieUrl || "",
    audio: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        id: item?.audio?.[language.iso]?.id || "",
        url: item?.audio?.[language.iso]?.url || "",
      };
      return acc;
    }, {}),
    heading: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        id: item?.heading?.[language.iso]?.id || "",
        text: item?.heading?.[language.iso]?.text || "",
      };
      return acc;
    }, {}),
    title: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        id: item?.title?.[language.iso]?.id || "",
        text: item?.title?.[language.iso]?.text || "",
      };
      return acc;
    }, {}),
    sub_title: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        id: item?.subTitle?.[language.iso]?.id || "",
        text: item?.subTitle?.[language.iso]?.text || "",
      };
      return acc;
    }, {}),
    primary_btn: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        id: item?.primaryBtn?.[language.iso]?.id || "",
        text: item?.primaryBtn?.[language.iso]?.text || "",
      };
      return acc;
    }, {}),
  }));
}

function isAllowedAudioFile(file) {
  const mimeType = String(file?.type || "").toLowerCase();
  const fileName = String(file?.name || "").toLowerCase();
  if (mimeType.startsWith("audio/")) return true;
  return [".mp3", ".wav", ".ogg", ".aac", ".m4a"].some((extension) =>
    fileName.endsWith(extension),
  );
}

function ContentSection() {
  const navigate = useNavigate();
  const { languages, loading, error } = useLanguages();
  const languageList = useMemo(() => buildLanguageList(languages), [languages]);
  const [onBoardingItems, setOnBoardingItems] = useState([]);
  const [activeLanguageIso, setActiveLanguageIso] = useState("");
  const [expandedItemId, setExpandedItemId] = useState("");
  const [deleteTargetItem, setDeleteTargetItem] = useState(null);
  const [fetchingOnBoardingItems, setFetchingOnBoardingItems] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");
  const [uploadingLottieById, setUploadingLottieById] = useState({});
  const [lottieErrorById, setLottieErrorById] = useState({});
  const [uploadingAudioByItemIso, setUploadingAudioByItemIso] = useState({});
  const [audioErrorByItemIso, setAudioErrorByItemIso] = useState({});
  const [editor, setEditor] = useState({
    isOpen: false,
    itemIndex: -1,
    languageIso: "",
  });
  const [subTitleHtml, setSubTitleHtml] = useState("");
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [quillHost, setQuillHost] = useState(null);
  const quillInstanceRef = useRef(null);
  const quillHandlerRef = useRef(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const currentLanguageIso = languageList.some(
    (language) => language.iso === activeLanguageIso,
  )
    ? activeLanguageIso
    : languageList[0]?.iso || "";
  const initialValues = useMemo(
    () => buildInitialValues(languageList, onBoardingItems),
    [onBoardingItems, languageList],
  );
  const validationSchema = useMemo(
    () => buildValidationSchema(languageList),
    [languageList],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setSubmitting(true);
      setLocalError("");
      try {
        const response = await saveOnBoardingsRequest(
          buildOnBoardingPayload(values?.items || [], languageList),
        );
        setOnBoardingItems(Array.isArray(response?.body) ? response.body : []);
        setExpandedItemId(
          (current) => current || formik.values?.items?.[0]?.clientId || "",
        );
      } catch (submitError) {
        setLocalError(
          submitError?.message || "Could not save on boarding items.",
        );
        throw submitError;
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    let isMounted = true;
    async function fetchOnBoardingItems() {
      setFetchingOnBoardingItems(true);
      setLocalError("");
      try {
        const response = await listOnBoardingsRequest();
        if (!isMounted) return;
        setOnBoardingItems(Array.isArray(response?.body) ? response.body : []);
      } catch (fetchError) {
        if (!isMounted) return;
        setLocalError(
          fetchError?.message || "Could not load on boarding items.",
        );
      } finally {
        if (isMounted) setFetchingOnBoardingItems(false);
      }
    }
    fetchOnBoardingItems();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!languageList.length) return;
    const firstItemId = formik.values?.items?.[0]?.clientId || "";
    if (firstItemId) setExpandedItemId((current) => current || firstItemId);
    formik.setTouched(
      buildTouchedValues(formik.values?.items || [], languageList),
      true,
    );
  }, [initialValues, languageList]);

  function getFieldError(path) {
    const touched = getIn(formik.touched, path);
    const fieldError = getIn(formik.errors, path);
    return touched ? fieldError || "" : "";
  }

  function updateLocalizedField(index, fieldName, languageIso, nextValue) {
    formik.setFieldValue(
      `items.${index}.${fieldName}.${languageIso}.text`,
      nextValue,
      true,
    );
  }

  function openSubTitleEditor(itemIndex, languageIso, value = "") {
    setEditor({
      isOpen: true,
      itemIndex,
      languageIso,
    });
    setSubTitleHtml(value || "");
  }

  function closeSubTitleEditor() {
    setEditor({
      isOpen: false,
      itemIndex: -1,
      languageIso: "",
    });
    setIsEditorReady(false);
  }

  function saveSubTitleEditor() {
    if (editor.itemIndex < 0 || !editor.languageIso) return;
    formik.setFieldValue(
      `items.${editor.itemIndex}.subTitle.${editor.languageIso}.text`,
      subTitleHtml,
      true,
    );
    formik.setFieldTouched(
      `items.${editor.itemIndex}.subTitle.${editor.languageIso}.text`,
      true,
      true,
    );
    closeSubTitleEditor();
  }

  function reorderWithOrder(list = []) {
    return list.map((item, index) => ({ ...item, order: index + 1 }));
  }

  function handleAddItem() {
    const nextItems = reorderWithOrder([
      ...(formik.values?.items || []),
      createEmptyOnBoardingItem(
        languageList,
        (formik.values?.items?.length || 0) + 1,
      ),
    ]);
    formik.setFieldValue("items", nextItems, true);
    setExpandedItemId(nextItems[nextItems.length - 1]?.clientId || "");
  }

  function handleRemoveItem(itemClientId) {
    const removedItem = (formik.values?.items || []).find(
      (item) => item.clientId === itemClientId,
    );
    if (!removedItem) return;
    setDeleteTargetItem(removedItem);
  }

  function handleDeleteLocalItem(itemClientId) {
    const nextItems = reorderWithOrder(
      (formik.values?.items || []).filter(
        (item) => item.clientId !== itemClientId,
      ),
    );
    formik.setFieldValue("items", nextItems, true);
    setOnBoardingItems(buildOnBoardingPayload(nextItems, languageList));
    setExpandedItemId((current) =>
      current === itemClientId ? nextItems[0]?.clientId || "" : current,
    );
  }

  async function handleDeletePersistedItem(item) {
    if (!item?.id) return;
    setSubmitting(true);
    setLocalError("");
    try {
      await deleteOnBoardingRequest(item.id);
      const nextItems = reorderWithOrder(
        (formik.values?.items || []).filter(
          (currentItem) => currentItem.clientId !== item.clientId,
        ),
      );
      setOnBoardingItems(buildOnBoardingPayload(nextItems, languageList));
      formik.setFieldValue("items", nextItems, true);
      setExpandedItemId((current) =>
        current === item.clientId ? nextItems[0]?.clientId || "" : current,
      );
    } catch (deleteError) {
      setLocalError(
        deleteError?.message || "Could not delete on boarding item.",
      );
    } finally {
      setDeleteTargetItem(null);
      setSubmitting(false);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTargetItem) return;
    if (deleteTargetItem?.id) {
      await handleDeletePersistedItem(deleteTargetItem);
      return;
    }
    handleDeleteLocalItem(deleteTargetItem.clientId);
    setDeleteTargetItem(null);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) return;
    const currentItems = [...(formik.values?.items || [])];
    const oldIndex = currentItems.findIndex(
      (item) => item.clientId === active.id,
    );
    const newIndex = currentItems.findIndex(
      (item) => item.clientId === over.id,
    );
    if (oldIndex < 0 || newIndex < 0) return;
    formik.setFieldValue(
      "items",
      reorderWithOrder(arrayMove(currentItems, oldIndex, newIndex)),
      true,
    );
  }

  async function handleUploadLottie(itemClientId, itemIndex, files = []) {
    const [file] = Array.isArray(files) ? files : [];
    if (!file) return;
    setUploadingLottieById((prev) => ({ ...prev, [itemClientId]: true }));
    setLottieErrorById((prev) => ({ ...prev, [itemClientId]: "" }));
    try {
      const response = await createMultimediaRequest({ file });
      const body = response?.body || {};
      const rawItem = Array.isArray(body?.multimediaList)
        ? body.multimediaList[0]
        : Array.isArray(body?.multimedia)
          ? body.multimedia[0]
          : body?.multimedia || null;
      if (!rawItem) throw new Error("Could not upload lottie file.");
      const uploadedUrl = rawItem?.url || rawItem?.mmd_url || "";
      const media = {
        id: rawItem?.id || rawItem?.mmd_id || "",
        sourceId: rawItem?.id || rawItem?.mmd_id || "",
        url: resolveMediaUrl(uploadedUrl),
        mediaType:
          rawItem?.mediaType ||
          rawItem?.mmd_media_type ||
          inferMediaType(uploadedUrl),
        mimeType: rawItem?.mimeType || rawItem?.mmd_mime_type || "",
      };
      formik.setFieldValue(`items.${itemIndex}.lottieFile`, media, false);
      formik.setFieldValue(
        `items.${itemIndex}.lottieUrl`,
        uploadedUrl || media.url || media.id || media.sourceId || "uploaded",
        true,
      );
    } catch (uploadError) {
      setLottieErrorById((prev) => ({
        ...prev,
        [itemClientId]: uploadError?.message || "Could not upload lottie file.",
      }));
      formik.setFieldTouched(`items.${itemIndex}.lottieUrl`, true, true);
    } finally {
      setUploadingLottieById((prev) => ({ ...prev, [itemClientId]: false }));
    }
  }

  async function handleUploadAudio(
    itemClientId,
    itemIndex,
    languageIso,
    files = [],
  ) {
    const [file] = Array.isArray(files) ? files : [];
    if (!file) return;
    const uploadKey = `${itemClientId}-${languageIso}`;
    if (!isAllowedAudioFile(file)) {
      setAudioErrorByItemIso((prev) => ({
        ...prev,
        [uploadKey]: "Only audio files are allowed for this section.",
      }));
      return;
    }
    setUploadingAudioByItemIso((prev) => ({ ...prev, [uploadKey]: true }));
    setAudioErrorByItemIso((prev) => ({ ...prev, [uploadKey]: "" }));
    try {
      const response = await createMultimediaRequest({ file });
      const body = response?.body || {};
      const rawItem = Array.isArray(body?.multimediaList)
        ? body.multimediaList[0]
        : Array.isArray(body?.multimedia)
          ? body.multimedia[0]
          : body?.multimedia || null;
      if (!rawItem) throw new Error("Could not upload audio.");
      const uploadedUrl = rawItem?.url || rawItem?.mmd_url || "";
      const media = {
        id: rawItem?.id || rawItem?.mmd_id || "",
        sourceId: rawItem?.id || rawItem?.mmd_id || "",
        url: resolveMediaUrl(uploadedUrl),
        mediaType:
          rawItem?.mediaType ||
          rawItem?.mmd_media_type ||
          inferMediaType(uploadedUrl),
        mimeType: rawItem?.mimeType || rawItem?.mmd_mime_type || "",
      };
      formik.setFieldValue(
        `items.${itemIndex}.audio.${languageIso}.media`,
        media,
        false,
      );
      formik.setFieldValue(
        `items.${itemIndex}.audio.${languageIso}.url`,
        uploadedUrl || media.url || media.id || media.sourceId || "uploaded",
        true,
      );
      formik.setFieldValue(
        `items.${itemIndex}.audio.${languageIso}.id`,
        media.id || media.sourceId || "",
        false,
      );
    } catch (uploadError) {
      setAudioErrorByItemIso((prev) => ({
        ...prev,
        [uploadKey]: uploadError?.message || "Could not upload audio.",
      }));
      formik.setFieldTouched(
        `items.${itemIndex}.audio.${languageIso}.url`,
        true,
        true,
      );
    } finally {
      setUploadingAudioByItemIso((prev) => ({ ...prev, [uploadKey]: false }));
    }
  }

  async function handleSave() {
    formik.setTouched(
      buildTouchedValues(formik.values?.items || [], languageList),
      true,
    );
    try {
      await formik.submitForm();
    } catch (errorValue) {}
  }

  useEffect(() => {
    if (!editor.isOpen) return;
    if (!quillHost || quillInstanceRef.current) return;
    const quill = createQuillEditor({
      container: quillHost,
      initialHtml: subTitleHtml,
    });
    if (!quill) return;
    quillHandlerRef.current = () => setSubTitleHtml(getQuillHtml(quill));
    quill.on("text-change", quillHandlerRef.current);
    quillInstanceRef.current = quill;
    setIsEditorReady(true);
  }, [editor.isOpen, quillHost, subTitleHtml]);

  useEffect(() => {
    if (!editor.isOpen && quillInstanceRef.current) {
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
      setIsEditorReady(false);
      setQuillHost(null);
    }
  }, [editor.isOpen, quillHost]);

  useEffect(
    () => () => {
      if (!quillInstanceRef.current) return;
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
    },
    [quillHost],
  );

  return (
    <section className="home-content-section-card">
      <div className="home-content-form-head">
        <div>
          <h3>Content</h3>
          <p>Manage the localized onboarding entries.</p>
        </div>
        {languageList.length ? (
          <div className="app-language-section">
            <div className="app-language-switcher" aria-label="Languages">
              {languageList.map((language) => (
                <button
                  key={language.id || language.iso}
                  type="button"
                  className={`app-language-btn ${currentLanguageIso === language.iso ? "is-active" : ""}`}
                  onClick={() => setActiveLanguageIso(language.iso)}
                  aria-pressed={currentLanguageIso === language.iso}
                  title={language.name || language.iso.toUpperCase()}
                >
                  {language.iso.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {loading || fetchingOnBoardingItems ? (
        <p className="form-helper">Loading data...</p>
      ) : null}
      {error ? <p className="message-error">{error}</p> : null}
      {localError ? <p className="message-error">{localError}</p> : null}

      {!loading && !fetchingOnBoardingItems && languageList.length ? (
        <form
          className="home-content-form tour-add-form"
          onSubmit={formik.handleSubmit}
        >
          <div className="home-content-form-shell">
            <div className="home-content-inline-actions">
              <button
                type="button"
                className="app-icon-btn app-icon-btn--add"
                aria-label="Add onboarding item"
                title="Add onboarding item"
                onClick={handleAddItem}
              >
                <IconPlus size={16} />
              </button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={(formik.values?.items || []).map(
                  (item) => item.clientId,
                )}
                strategy={verticalListSortingStrategy}
              >
                <div className="home-content-accordion-list">
                  {(formik.values?.items || []).map((item, index) => {
                    const audioKey = `${item.clientId}-${currentLanguageIso}`;
                    return (
                      <SortableCarbonItem
                        key={item.clientId}
                        item={{
                          ...item,
                          id: item.clientId,
                          title: languageList.reduce((acc, language) => {
                            acc[language.iso] =
                              item?.title?.[language.iso]?.text || "";
                            return acc;
                          }, {}),
                        }}
                        visibleLanguageIso={currentLanguageIso}
                        isOpen={expandedItemId === item.clientId}
                        onToggle={() =>
                          setExpandedItemId((current) =>
                            current === item.clientId ? "" : item.clientId,
                          )
                        }
                        onDelete={() => handleRemoveItem(item.clientId)}
                      >
                        <div className="home-content-form-grid home-content-form-grid--home">
                          <div className="tour-add-form-group">
                            <p className="tour-add-form-label">
                              id text
                              {getFieldError(`items.${index}.idText`) ? (
                                <InlineError>
                                  {getFieldError(`items.${index}.idText`)}
                                </InlineError>
                              ) : null}
                            </p>
                            <label className="field-label app-form-field">
                              <input
                                className="field-input"
                                value={item?.idText || ""}
                                placeholder="id text"
                                disabled="disabled"
                                onChange={(event) =>
                                  formik.setFieldValue(
                                    `items.${index}.idText`,
                                    event.target.value,
                                    true,
                                  )
                                }
                                onBlur={() =>
                                  formik.setFieldTouched(
                                    `items.${index}.idText`,
                                    true,
                                    true,
                                  )
                                }
                              />
                            </label>
                          </div>

                          <div className="tour-add-form-group">
                            <p className="tour-add-form-label">
                              lottie
                              {getFieldError(`items.${index}.lottieUrl`) ? (
                                <InlineError>
                                  {getFieldError(`items.${index}.lottieUrl`)}
                                </InlineError>
                              ) : null}
                            </p>
                            <MediaPicker
                              title="lottie (required)"
                              titleError=""
                              items={item?.lottieFile ? [item.lottieFile] : []}
                              isUploading={Boolean(
                                uploadingLottieById[item.clientId],
                              )}
                              uploadError={lottieErrorById[item.clientId] || ""}
                              accept={{ ".lottie": [] }}
                              maxFiles={1}
                              invalidTypeMessage="Only .lottie files are allowed for this section."
                              onRejected={(message) =>
                                setLottieErrorById((prev) => ({
                                  ...prev,
                                  [item.clientId]: message,
                                }))
                              }
                              onUpload={(files) =>
                                handleUploadLottie(item.clientId, index, files)
                              }
                              onRemove={() => {
                                formik.setFieldValue(
                                  `items.${index}.lottieFile`,
                                  null,
                                  false,
                                );
                                formik.setFieldValue(
                                  `items.${index}.lottieUrl`,
                                  "",
                                  true,
                                );
                                formik.setFieldTouched(
                                  `items.${index}.lottieUrl`,
                                  true,
                                  true,
                                );
                              }}
                              uploadLabel="Upload lottie file"
                              uploadHint="Only 1 .lottie file is allowed for this section."
                            />
                          </div>

                          <div className="tour-add-form-group">
                            <p className="tour-add-form-label">
                              audio
                              {getFieldError(
                                `items.${index}.audio.${currentLanguageIso}.url`,
                              ) ? (
                                <InlineError>
                                  {getFieldError(
                                    `items.${index}.audio.${currentLanguageIso}.url`,
                                  )}
                                </InlineError>
                              ) : null}
                            </p>
                            <MediaPicker
                              title={`audio (${currentLanguageIso})`}
                              titleError=""
                              items={
                                item?.audio?.[currentLanguageIso]?.media
                                  ? [item.audio[currentLanguageIso].media]
                                  : []
                              }
                              isUploading={Boolean(
                                uploadingAudioByItemIso[audioKey],
                              )}
                              uploadError={audioErrorByItemIso[audioKey] || ""}
                              accept={{
                                "audio/mpeg": [".mp3"],
                                "audio/wav": [".wav"],
                                "audio/ogg": [".ogg"],
                                "audio/aac": [".aac"],
                                "audio/mp4": [".m4a"],
                                "audio/x-m4a": [".m4a"],
                              }}
                              maxFiles={1}
                              invalidTypeMessage="Only audio files are allowed for this section."
                              onRejected={(message) =>
                                setAudioErrorByItemIso((prev) => ({
                                  ...prev,
                                  [audioKey]: message,
                                }))
                              }
                              onUpload={(files) =>
                                handleUploadAudio(
                                  item.clientId,
                                  index,
                                  currentLanguageIso,
                                  files,
                                )
                              }
                              onRemove={() => {
                                formik.setFieldValue(
                                  `items.${index}.audio.${currentLanguageIso}.media`,
                                  null,
                                  false,
                                );
                                formik.setFieldValue(
                                  `items.${index}.audio.${currentLanguageIso}.url`,
                                  "",
                                  true,
                                );
                                formik.setFieldValue(
                                  `items.${index}.audio.${currentLanguageIso}.id`,
                                  "",
                                  false,
                                );
                                formik.setFieldTouched(
                                  `items.${index}.audio.${currentLanguageIso}.url`,
                                  true,
                                  true,
                                );
                              }}
                              uploadLabel={`Upload audio (${currentLanguageIso})`}
                              uploadHint="Only 1 audio is allowed for this language."
                            />
                          </div>

                          {LOCALIZED_ON_BOARDING_FIELDS.map((field) =>
                            field.name === "subTitle" ? (
                              <div
                                className="tour-add-form-group"
                                key={`${item.clientId}-${field.name}`}
                              >
                                <p className="tour-add-form-label">
                                  {field.label}
                                  {getFieldError(
                                    `items.${index}.${field.name}.${currentLanguageIso}.text`,
                                  ) ? (
                                    <InlineError>
                                      {getFieldError(
                                        `items.${index}.${field.name}.${currentLanguageIso}.text`,
                                      )}
                                    </InlineError>
                                  ) : null}
                                </p>
                                <button
                                  type="button"
                                  className={`field-label app-form-field field-preview-row ${
                                    item?.subTitle?.[currentLanguageIso]?.text
                                      ? "filled"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    openSubTitleEditor(
                                      index,
                                      currentLanguageIso,
                                      item?.subTitle?.[currentLanguageIso]
                                        ?.text || "",
                                    )
                                  }
                                >
                                  {item?.subTitle?.[currentLanguageIso]
                                    ?.text ? (
                                    <span
                                      className="field-preview-value field-preview-value-html"
                                      dangerouslySetInnerHTML={{
                                        __html: String(
                                          item?.subTitle?.[currentLanguageIso]
                                            ?.text || "",
                                        ),
                                      }}
                                    />
                                  ) : (
                                    <span className="field-preview-value">
                                      Complete
                                    </span>
                                  )}
                                </button>
                              </div>
                            ) : (
                              <HomeLocalizedField
                                key={`${item.clientId}-${field.name}`}
                                label={field.label}
                                fieldName={field.name}
                                activeLanguageIso={currentLanguageIso}
                                value={
                                  item?.[field.name]?.[currentLanguageIso]
                                    ?.text || ""
                                }
                                onChange={(_, languageIso, nextValue) =>
                                  updateLocalizedField(
                                    index,
                                    field.name,
                                    languageIso,
                                    nextValue,
                                  )
                                }
                                onBlur={(_, languageIso) =>
                                  formik.setFieldTouched(
                                    `items.${index}.${field.name}.${languageIso}.text`,
                                    true,
                                    true,
                                  )
                                }
                                error={getFieldError(
                                  `items.${index}.${field.name}.${currentLanguageIso}.text`,
                                )}
                                placeholder={`(${currentLanguageIso})`}
                              />
                            ),
                          )}
                        </div>
                      </SortableCarbonItem>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          <div className="mode-route-tool-steps tour-add-save-row">
            <button
              type="button"
              className="app-icon-btn app-icon-btn--back mode-route-tool-route-btn on-boarding-close-btn"
              onClick={() => navigate("/tour")}
              aria-label="Close on boarding"
              title="Close on boarding"
            >
              Close
              <IconX size={16} />
            </button>
            <button
              type="button"
              className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${!formik.isValid || submitting ? "is-disabled" : ""} ${submitting ? "is-loading" : ""}`}
              onClick={handleSave}
              disabled={!formik.isValid || submitting}
              aria-disabled={!formik.isValid || submitting}
              aria-label="Save on boarding content"
              title="Save on boarding content"
            >
              {submitting ? (
                <span className="mode-route-tool-btn-spinner" />
              ) : (
                <>
                  Save
                  <IconDeviceFloppy size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      ) : null}

      <AppModal
        isOpen={editor.isOpen}
        onClose={closeSubTitleEditor}
        title={`sub title (${editor.languageIso})`}
        size="lg"
      >
        <div className="tour-add-editor">
          {!isEditorReady ? (
            <p className="tour-add-editor-loading">Loading editor...</p>
          ) : null}
          <div ref={setQuillHost} className="tour-add-quill" />
          <div className="tour-add-editor-actions">
            <button
              type="button"
              className="button-primary"
              onClick={saveSubTitleEditor}
            >
              Save
            </button>
            <button
              type="button"
              className="button-ghost"
              onClick={closeSubTitleEditor}
            >
              Cancel
            </button>
          </div>
        </div>
      </AppModal>

      <ConfirmDeleteModal
        isOpen={Boolean(deleteTargetItem)}
        onClose={() => {
          if (submitting) return;
          setDeleteTargetItem(null);
        }}
        onConfirm={handleConfirmDelete}
        submitting={submitting}
        title="Delete onboarding item"
        description={
          <>
            Are you sure you want to delete{" "}
            <span>{deleteTargetItem?.title?.en?.text || ""}</span> onboarding
            item?
          </>
        }
      />
    </section>
  );
}

export default ContentSection;
